/**
 * AncestryScreen — two-card design.
 *
 * Card 0: Your own background (multi-select)
 * Card 1: Open to in a partner (multi-select, including "any")
 *
 * Ancestry is a preference only — never a gate. The plain-language line
 * from the brief is shown on both cards, as required.
 */
import { useState } from "react";
import { Button } from "../components/Button";
import { ORIGIN_ANY, ORIGIN_OPTIONS } from "../domain/questionnaireOptions";
import { useFlow } from "../flow/FlowContext";
import { useT } from "../i18n";
import { QuestionCard } from "../components/QuestionCard";

function originMessageKey(code: string): string {
  if (code === "middle_eastern") return "middleEastern";
  return code;
}

const TOTAL = 2;

function AncestryCheckList({
  codes,
  selected,
  onToggle,
}: {
  codes: string[];
  selected: string[];
  onToggle: (code: string, on: boolean) => void;
}) {
  const t = useT();
  return (
    <div className="q-ancestry">
      {codes.map((code) => {
        const isSelected = selected.includes(code);
        return (
          <button
            key={code}
            type="button"
            className={`q-ancestry__item${isSelected ? " is-selected" : ""}`}
            onClick={() => onToggle(code, !isSelected)}
            aria-pressed={isSelected}
          >
            <span className="q-ancestry__check">{isSelected ? "✓" : ""}</span>
            <span className="q-ancestry__label">{t(`options.origin.${originMessageKey(code)}`)}</span>
          </button>
        );
      })}
    </div>
  );
}

export function AncestryScreen() {
  const { profileAnswers, setProfileAnswers, next, canContinue } = useFlow();
  const t = useT();
  const { own, accepted } = profileAnswers.ancestry;

  const [card, setCard] = useState(0);
  const pos = t("questionnaire.progressLabel", { current: String(card + 1), total: String(TOTAL) });

  const toggleOwn = (code: string, on: boolean) => {
    setProfileAnswers((p) => {
      const nextOwn = on
        ? p.ancestry.own.includes(code) ? p.ancestry.own : [...p.ancestry.own, code]
        : p.ancestry.own.filter((c) => c !== code);
      return { ...p, ancestry: { ...p.ancestry, own: nextOwn } };
    });
  };

  const toggleAccepted = (code: string, on: boolean) => {
    setProfileAnswers((p) => {
      let nextAccepted = [...p.ancestry.accepted];
      if (code === ORIGIN_ANY.code) {
        nextAccepted = on ? [ORIGIN_ANY.code] : nextAccepted.filter((c) => c !== ORIGIN_ANY.code);
      } else {
        nextAccepted = nextAccepted.filter((c) => c !== ORIGIN_ANY.code);
        if (on) {
          if (!nextAccepted.includes(code)) nextAccepted.push(code);
        } else {
          nextAccepted = nextAccepted.filter((c) => c !== code);
        }
      }
      return { ...p, ancestry: { ...p.ancestry, accepted: nextAccepted } };
    });
  };

  /* ── Card 0: Your own background ── */
  if (card === 0) {
    return (
      <div className="screen-enter" style={{ flex: 1, overflowY: "auto" }}>
        <QuestionCard
          question={t("ancestry.yourBackground")}
          subtext={t("questionnaire.ancestryNote")}
          position={pos}
        >
          <AncestryCheckList
            codes={ORIGIN_OPTIONS.map((o) => o.code)}
            selected={own}
            onToggle={toggleOwn}
          />
        </QuestionCard>
        <div className="shell__footer" style={{ paddingLeft: 0, paddingRight: 0 }}>
          <Button onClick={() => setCard(1)} disabled={own.length === 0}>
            {t("questionnaire.confirm")}
          </Button>
        </div>
      </div>
    );
  }

  /* ── Card 1 (last): Open to in a partner ── */

  return (
    <div className="screen-enter" style={{ flex: 1, overflowY: "auto" }}>
      <QuestionCard
        question={t("ancestry.openToPartner")}
        subtext={t("questionnaire.ancestryNote")}
        position={pos}
      >
        <div className="q-ancestry">
          {/* "Any / no preference" option first */}
          <button
            type="button"
            className={`q-ancestry__item${accepted.includes(ORIGIN_ANY.code) ? " is-selected" : ""}`}
            onClick={() => toggleAccepted(ORIGIN_ANY.code, !accepted.includes(ORIGIN_ANY.code))}
            aria-pressed={accepted.includes(ORIGIN_ANY.code)}
          >
            <span className="q-ancestry__check">{accepted.includes(ORIGIN_ANY.code) ? "✓" : ""}</span>
            <span className="q-ancestry__label">{t("options.origin.any")}</span>
          </button>
          <AncestryCheckList
            codes={ORIGIN_OPTIONS.map((o) => o.code)}
            selected={accepted.includes(ORIGIN_ANY.code) ? [] : accepted}
            onToggle={toggleAccepted}
          />
        </div>
        <button type="button" className="q-subnav__back" style={{ marginTop: 16 }} onClick={() => setCard(0)}>
          ← {t("shell.back")}
        </button>
      </QuestionCard>
      <div className="shell__footer" style={{ paddingLeft: 0, paddingRight: 0 }}>
        <Button onClick={next} disabled={!canContinue}>
          {t("common.continue")}
        </Button>
      </div>
    </div>
  );
}

